/**
 * publicPaths — the single definition of which pathnames render WITHOUT beta
 * access (through the waitlist gate). Extracted from App.js on 2026-09-04 so
 * the api-client 401 interceptor can consult the same list instead of
 * hard-redirecting public-page visitors into a gated route.
 *
 * Intentionally NOT whitelisted: /welcome (would expose the marketing
 * landing page to non-beta visitors), /register (new signups must go
 * through the waitlist), /accept-invite (an org invite IS a backdoor to
 * platform access — we want full control during beta), /login (the gate
 * carries its own sign-in modal).
 */

// Forgot/verify/terms/privacy are navigation-based and need to be reachable
// for password recovery, email verification links, and legal page links
// from emails.
export const PUBLIC_AUTH_PATHS = [
  '/forgot-password',
  '/verify',
  '/terms',
  '/privacy',
  // DEC-1363: the waitlist gate's nav links here, so it must render through
  // the gate. This is a deliberate narrowing of the rule above — /about is
  // the ONE marketing surface a non-beta visitor may reach. It carries static
  // copy only: no program data, no counts, no platform state. Do not extend
  // this exception to /welcome.
  '/about',
  // DEC-922: the Option-B break-out lands here from a third-party embed. The
  // single-use hand-off token in ?t= is the authentication, so it must render
  // before/around the beta gate exactly like /verify. Precise path only — no
  // other /enroll/* route is whitelisted.
  '/enroll/continue',
  // dec804: the re-engagement magic-login link lands here. The single-use token
  // in ?t= is the authentication; the page consumes it, mints the session, sets
  // the deco_beta cookie, then hard-navigates to the user's site. Must render
  // through the gate (the consume response is what grants beta access).
  '/magic',
];

// DEC-723: Path prefixes (not exact matches) that must render even when
// the beta gate is up. `/upload/<token>` is the delegated upload-link
// flow used by external service providers, contractors, inspectors, etc.
// to drop documents on a specific site/task — they explicitly do NOT
// have DECO accounts. The token itself is the authentication; the page
// only ever shows the metadata of one specific task scoped to that
// token, so there's no platform leak.
export const PUBLIC_PREFIX_PATHS = [
  '/upload/',
];

// DEC-920: the public utility program page lives under /programs/:slug, but
// /programs/* ALSO contains authenticated operator routes (/programs and
// /programs/:id/operations). We whitelist ONLY the single-segment public shape
// so the gate keeps protecting the operator sub-routes.
//   PUBLIC:  /programs/pge
//   GATED:   /programs, /programs/abc/operations
// Case-insensitive to match the router (2026-09-04: it wasn't, so /Programs/pge
// gated while /programs/PGE rendered — inconsistent with the two-segment rule).
export const PUBLIC_UTILITY_PAGE_RE = /^\/programs\/[^/]+$/i;

// DEC-1273: the direct program link (/programs/:slug/:programId) is public.
// ux review F2: a MANGLED link (truncated by a CMS, hand-retyped) must reach
// the page's own friendly not-found state — a strict-UUID whitelist dumped
// those visitors onto the beta WAITLIST form instead. So: any two-segment
// /programs/x/y is public EXCEPT the reserved operator literals below.
// ⚠️ Keep RESERVED_PROGRAM_SUBPATHS in sync with any /programs/:id/<literal>
// operator route added to AppRoutes.
export const RESERVED_PROGRAM_SUBPATHS = ['operations'];
export const PUBLIC_DIRECT_PROGRAM_RE = new RegExp(
  `^/programs/[^/]+/(?!(?:${RESERVED_PROGRAM_SUBPATHS.join('|')})$)[^/]+$`, 'i'
);

/**
 * React Router matches `/programs/pge/<uuid>/` (its patterns tolerate
 * trailing slashes) but a raw regex with `[^/]+$` does not — served that way,
 * a CMS- or mail-client-appended trailing slash routed the visitor to the
 * program page component while the GATE saw a non-whitelisted path and showed
 * the waitlist instead (found 2026-09-04, PG&E pilot link investigation).
 * Normalize exactly the way the router does: strip trailing slashes.
 */
const normalizePathname = (pathname) => {
  const p = String(pathname || '').replace(/\/+$/, '');
  return p === '' ? '/' : p;
};

export const isPublicAuthPath = (pathname) => {
  const p = normalizePathname(pathname);
  return (
    PUBLIC_AUTH_PATHS.includes(p) ||
    PUBLIC_UTILITY_PAGE_RE.test(p) ||
    PUBLIC_DIRECT_PROGRAM_RE.test(p) ||
    PUBLIC_PREFIX_PATHS.some((prefix) => p.startsWith(prefix))
  );
};
